import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { GsdReadableBlock, useI18n } from "@/coquille-bridge";
import { DemoBadge } from "@/components/DemoBadge.jsx";
import { useAuth } from "@/context/AuthContext.jsx";
import { getTrainingTracksForRole, getTrainingUi } from "@/content/trainingContent.js";

/** Parcours statiques BT-032 — élève, parent, responsables (hors périmètre R0 live). */
export function StaticFormationPreview() {
  const { role, isAuthenticated } = useAuth();
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const ui = getTrainingUi(locale);
  const tracks = getTrainingTracksForRole(isAuthenticated ? role : "guest", locale);
  const [activeTrackId, setActiveTrackId] = useState(tracks[0]?.id || "students");
  const activeTrack = tracks.find((track) => track.id === activeTrackId) || tracks[0];

  return (
    <>
      <GsdReadableBlock className="formation-intro" testId="formation-intro">
        <h2>{ui.introTitle}</h2>
        <p>{ui.introBody}</p>
        {!isAuthenticated ? (
          <p className="muted">
            {ui.loginHint} <Link to="/connexion">{locale === "en" ? "Sign in" : "Connexion"}</Link>
          </p>
        ) : null}
      </GsdReadableBlock>

      <div className="formation-card-grid" data-testid="formation-track-cards">
        {tracks.map((track) => {
          const selected = track.id === activeTrack.id;
          return (
            <article
              key={track.id}
              className={selected ? "formation-card formation-card--active" : "formation-card"}
              data-testid={`formation-card-${track.id}`}
            >
              <p className="formation-card__role">{ui.roleLabels[track.role]}</p>
              <h2>{track.title}</h2>
              <p>{track.description}</p>
              <p className="muted">{track.duration}</p>
              <button
                type="button"
                className={selected ? "btn-primary" : "btn-primary-outline"}
                data-testid={`formation-open-${track.id}`}
                onClick={() => setActiveTrackId(track.id)}
                aria-pressed={selected}
              >
                {selected ? ui.selectedLabel : ui.cardButton}
              </button>
            </article>
          );
        })}
      </div>

      {activeTrack ? (
        <GsdReadableBlock className="formation-detail" testId={`formation-detail-${activeTrack.id}`}>
          <header className="formation-detail__header">
            <p className="formation-card__role">{ui.roleLabels[activeTrack.role]}</p>
            <p className="muted">{ui.detailStepsLead}</p>
          </header>

          <ol className="formation-steps" data-testid={`formation-steps-${activeTrack.id}`}>
            {activeTrack.sections.map((section, index) => (
              <li key={section.id} className="formation-step" data-testid={`formation-section-${section.id}`}>
                <h3>
                  {index + 1}. {section.title}
                </h3>
                <p>{section.body}</p>
                {section.links?.length ? (
                  <div className="formation-links">
                    <strong>{ui.routesLabel}</strong>
                    <ul className="link-list">
                      {section.links.map((link) => (
                        <li key={`${section.id}-${link.to}`}>
                          <Link to={link.to}>{link.label}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </li>
            ))}
          </ol>

          <div className="formation-info-grid">
            <section className="formation-callout" data-testid={`formation-know-${activeTrack.id}`}>
              <h3>{ui.knowTitle}</h3>
              <ul>
                {activeTrack.knowItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
            <section className="formation-callout formation-callout--demo" data-testid={`formation-limits-${activeTrack.id}`}>
              <h3>{ui.limitsTitle}</h3>
              <ul>
                {activeTrack.limitItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <DemoBadge />
            </section>
          </div>

          <section className="formation-checklist" data-testid={`formation-checklist-${activeTrack.id}`}>
            <h3>{activeTrack.checklist?.title || ui.checklistTitle}</h3>
            <ul>
              {(activeTrack.checklist?.items || []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        </GsdReadableBlock>
      ) : null}
    </>
  );
}
